import buttonStyles from '../../components/Button/Button.module.scss'
import { type FC } from 'react'
import { Col, Container, Row, Tab } from 'react-bootstrap'
import { UserAvatar } from '../../components/UserAvatar/UserAvatar'
import { useUserAvatarProps } from '../../components/UserAvatar/useUserAvatarProps'
import { ProfileSettingsTab } from './ProfileSettingsTab'
import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Tabs } from '../../components/Tabs/Tabs'
import { useAppSelector } from '../../store/hooks'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { useGetMeQuery } from '../../store/user/user'
import { selectAccountId } from '../../store/auth/authSlice'
import { getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { MeetingAssistantTab } from './MeetingAssistantTab'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { CollaborationTab } from './Collaboration/CollaborationTab'
import PrizeBadge from '../../components/ScopedBlock/PrizeBadge.svg?url'
import { MFATab } from './MFATab'
import { useRedirectHiddenTab } from '../../common/useRedirectHiddenTab'
import { SchedulingProfileTab } from './Scheduling/SchedulingProfileTab'

export const ProfileSettings: FC = () => {
  useDocumentTitle('Profile')
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId: accountId })
  return (
    <Container fluid>
      <Row className='mb-9'>
        <Col className='p-0'>
          <Container>
            <Row>
              <Col xs='auto'>
                <UserAvatar {...useUserAvatarProps({ userId: currentUser?.id, width: '72px', height: '72px', fontSize: '40px' })} />
              </Col>
              <Col>
                <Row>
                  <Col>
                    <h3>{currentUser ? getUserDisplayName(currentUser) : null}</h3>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className='p-medium zocks-text-gray-600'>{currentUser?.email}</div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </Col>
        <Col className='d-none d-md-flex align-items-center ' xs='auto'>
          <NavLink className={`${buttonStyles.button} ${buttonStyles.primaryGhostButton}`} to={'/account/manage-team'}>
            View all people
          </NavLink>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <ProfileSettingsTabs />
        </Col>
      </Row>
    </Container>
  )
}

export const ProfileSettingsTabs: FC = () => {
  // The URL param is the single source of truth for the selected tab; the previous
  // state + two-way sync effects tripped react-hooks/set-state-in-effect.
  const { activeTab: selectedTab = 'profile-settings' } = useParams()

  const navigate = useNavigate()

  const { hasQuota, isFetching: isQuotaFetching } = useZocksQuota()
  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')
  const isSchedulingTabVisible = hasQuota('scheduling.enabled.value')
  const [searchParams] = useSearchParams()

  useRedirectHiddenTab({
    currentKey: selectedTab,
    hiddenKeys: [!isSchedulingTabVisible && 'scheduling'],
    isLoading: isQuotaFetching,
    fallbackPath: '/account/profile-settings/profile-settings',
  })

  const changeTab = (key: string | null) => {
    if (key) {
      navigate(
        {
          pathname: `/account/profile-settings/${key}`,
          search: searchParams.toString(),
        },
        { replace: true },
      )
    }
  }

  const collaborationTitle = (
    <div className='d-flex align-items-center'>
      {!sessionSharingAvailable ? <img alt='Upgrade' src={PrizeBadge} /> : ''}
      Collaboration
    </div>
  )

  return (
    <Tabs className='mb-6' onSelect={changeTab} id='profile-settings-tabs' activeKey={selectedTab}>
      <Tab eventKey='profile-settings' id='Profile Settings' title='Profile Settings'>
        <ProfileSettingsTab />
      </Tab>
      {/*<Tab eventKey='notifications' id='Notifications' title='Notifications'>*/}
      {/*  <NotificationsTab />*/}
      {/*</Tab>*/}
      <Tab eventKey='meeting-assistant' id='Meeting Assistant' title='Meeting Assistant'>
        <MeetingAssistantTab />
      </Tab>
      <Tab eventKey='collaboration' id='Collaboration' title={collaborationTitle}>
        <CollaborationTab />
      </Tab>
      {isSchedulingTabVisible && (
        <Tab eventKey='scheduling' id='Scheduling' title='Scheduling'>
          <SchedulingProfileTab />
        </Tab>
      )}
      <Tab eventKey='mfa' id='MFA' title='2-factor authentication'>
        <MFATab />
      </Tab>
    </Tabs>
  )
}
